import * as PIXI from "pixi.js";
import {config} from "../../../../config";
import {RouletteBetsBuilder} from "../../../api/RouletteBetsBuilder";
import {RouletteBets} from "../../../api/RouletteBetsProtocol";
import {ColorBet} from "../../../betTypes/ColorBet";
import {ColumnBet} from "../../../betTypes/ColumnBet";
import {CornerBet} from "../../../betTypes/CornerBet";
import {DozenBet} from "../../../betTypes/DozenBet";
import {EvenOddBet} from "../../../betTypes/EvenOddBet";
import {LineBet} from "../../../betTypes/LineBet";
import {LowHighNumbersBet} from "../../../betTypes/LowHighNumbersBet";
import {SplitBet} from "../../../betTypes/SplitBet";
import {StraightBet} from "../../../betTypes/StraightBet";
import {StreetBet} from "../../../betTypes/StreetBet";
import {InfoSnackBar} from "../../../InfoSnackBar";
import {ChipsStacks} from "../../ChipsStacks";
import {Footer} from "../../footer/Footer";
import {IChipsStacks} from "../../IChipsStacks";
import {SmallBetsChipsStacks} from "../../SmallBetsChipsStacks";
import {IRouletteBetsPanel} from "../IRouletteBetsPanel";
import {ColorBetsPanel} from "./ColorBetsPanel";
import {ColumnBetsPanel} from "./ColumnBetsPanel";
import {CornerBetsPanel} from "./CornerBetsPanel";
import {DozenBetsPanel} from "./DozenBetsPanel";
import {EvenOddBetsPanel} from "./EvenOddBetsPanel";
import {LineBetsPanel} from "./LineBetsPanel";
import {LowHighNumbersBetsPanel} from "./LowHighNumbersBetsPanel";
import {SplitBetsPanel} from "./SplitBetsPanel";
import {StraightBetsPanel} from "./StraightBetsPanel";
import {StreetBetsPanel} from "./StreetBetsPanel";

export class RouletteBetsPanel extends PIXI.Container implements IRouletteBetsPanel {
    private readonly STRAIGHT_BETS_BUTTONS_OFFSET_X = 83;

    private readonly STRAIGHT_BETS_BUTTONS_OFFSET_Y = 105;
    private readonly STRAIGHT_BETS_OFFSET_FROM_ZERO = 90;
    private readonly LOW_NUMBERS_BET_POSITION_X = 132;

    private readonly EVEN_BET_POSITION_X = 298;
    private readonly RED_COLOR_BET_POSITION_X = 464;
    private readonly BLACK_COLOR_BET_POSITION_X = 630;
    private readonly ODD_BET_POSITION_X = 796;
    private readonly HIGH_NUMBERS_BET_POSITION_X = 962;
    private readonly DOZEN_BETS_POSITION_Y = 295;

    private readonly NO_LAST_BET_POPUP_POSITION = new PIXI.Point(220, 430);
    private readonly CLEAR_BETS_POPUP_POSITION = new PIXI.Point(325, 430);
    private readonly RESTORE_BETS_POPUP_POSITION = new PIXI.Point(770, 430);

    private readonly EVEN_ODD_COLOR_LOW_HIGH_BETS_POSITION_Y = 360;
    private straightBetsPanel: StraightBetsPanel;

    private splitBetsPanel: SplitBetsPanel;
    private cornerBetsPanel: CornerBetsPanel;
    private lineBetsPanel: LineBetsPanel;
    private streetBetsPanel: StreetBetsPanel;
    private columnBetsPanel: ColumnBetsPanel;
    private dozenBetsPanel: DozenBetsPanel;
    private lowHighNumbersBetsPanel: LowHighNumbersBetsPanel;
    private evenOddBetsPanel: EvenOddBetsPanel;
    private colorBetsPanel: ColorBetsPanel;
    private chipsStacks: IChipsStacks;
    private infoSnackBar: InfoSnackBar;
    private previousRouletteBetsHistory: Array<StraightBet | SplitBet | CornerBet | StreetBet | LineBet | ColumnBet
        | DozenBet | LowHighNumbersBet | EvenOddBet | ColorBet> = [];
    private rouletteBetsHistory: Array<StraightBet | SplitBet | CornerBet | StreetBet | LineBet | ColumnBet
        | DozenBet | LowHighNumbersBet | EvenOddBet | ColorBet> = [];
    private rouletteBetsBuilder: RouletteBetsBuilder = new RouletteBetsBuilder();
    private betValue: number = 0.1;
    private balanceValue: number;

    constructor() {
        super();
        this.createAndAddDozenBetsPanel();
        this.createAndAddLowHighNumbersBetsPanel();
        this.createAndAddEvenOddBetsPanel();
        this.createAndAddColorBetsPanel();
        this.createAndAddStraightBetsPanel();
        this.createAndAddSplitBetsPanel();
        this.createAndAddCornerBetsPanel();
        this.createAndAddLineBetsPanel();
        this.createAndAddStreetBetsPanel();
        this.createAndAddColumnBetsPanel();
        this.createAndAddChipsStacks();
        this.createAndAddInfoSnackBar();
    }

    public enableBetPanel(): void {
        this.straightBetsPanel.visible = true;
        this.splitBetsPanel.visible = true;
        this.cornerBetsPanel.visible = true;
        this.lineBetsPanel.visible = true;
        this.streetBetsPanel.visible = true;
        this.columnBetsPanel.visible = true;
        this.dozenBetsPanel.visible = true;
        this.lowHighNumbersBetsPanel.visible = true;
        this.evenOddBetsPanel.visible = true;
        this.colorBetsPanel.visible = true;
    }

    public disableBetPanel(): void {
        this.straightBetsPanel.visible = false;
        this.splitBetsPanel.visible = false;
        this.cornerBetsPanel.visible = false;
        this.lineBetsPanel.visible = false;
        this.streetBetsPanel.visible = false;
        this.columnBetsPanel.visible = false;
        this.dozenBetsPanel.visible = false;
        this.lowHighNumbersBetsPanel.visible = false;
        this.evenOddBetsPanel.visible = false;
        this.colorBetsPanel.visible = false;
    }

    public getRouletteBets(): RouletteBets {
        return this.rouletteBetsBuilder.build();
    }

    public setBetValue(value: number): void {
        this.betValue = value;
    }

    public setBalanceValue(value: number): void {
        this.balanceValue = value;
    }

    public clearRouletteBets(showError: boolean = true): void {
        if (this.rouletteBetsHistory.length === 0) {
            if (showError) {
                this.infoSnackBar.show("No bets to clear.", this.CLEAR_BETS_POPUP_POSITION);
            }
            return;
        }
        this.chipsStacks.removeAllStacks();
        this.rouletteBetsBuilder.clear();
        Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
        this.previousRouletteBetsHistory = this.rouletteBetsHistory;
        this.rouletteBetsHistory = [];
        this.infoSnackBar.show("All bets cleared.", this.CLEAR_BETS_POPUP_POSITION);
    }

    public restoreBets(): void {
        if (this.previousRouletteBetsHistory.length === 0) {
            this.infoSnackBar.show("No bets to restore.", this.RESTORE_BETS_POPUP_POSITION);
            return;
        }
        let bet;
        this.rouletteBetsHistory = this.previousRouletteBetsHistory;
        this.previousRouletteBetsHistory = [];
        for (let i = 0; i < this.rouletteBetsHistory.length; i++) {
            bet = this.rouletteBetsHistory[i];
            if (bet) {
                if (bet instanceof StraightBet) {
                    this.rouletteBetsBuilder.addStraightBet(bet.chosenNumber, bet.betAmount);
                    this.chipsStacks.setStraightBetChipsStack(bet.chosenNumber,
                        this.rouletteBetsBuilder.getStraightBet(bet.chosenNumber).betAmount);
                } else if (bet instanceof SplitBet) {
                    this.rouletteBetsBuilder.addSplitBet(bet.index, bet.betAmount);
                    this.chipsStacks.setSplitBetChipsStack(bet.index,
                        this.rouletteBetsBuilder.getSplitBet(bet.index).betAmount);
                } else if (bet instanceof CornerBet) {
                    this.rouletteBetsBuilder.addCornerBet(bet.index, bet.betAmount);
                    this.chipsStacks.setCornerBetChipsStack(bet.index,
                        this.rouletteBetsBuilder.getCornerBet(bet.index).betAmount);
                } else if (bet instanceof StreetBet) {
                    this.rouletteBetsBuilder.addStreetBet(bet.index, bet.betAmount);
                    this.chipsStacks.setStreetBetChipsStack(bet.index,
                        this.rouletteBetsBuilder.getStreetBet(bet.index).betAmount);
                } else if (bet instanceof LineBet) {
                    this.rouletteBetsBuilder.addLineBet(bet.index, bet.betAmount);
                    this.chipsStacks.setLineBetChipsStack(bet.index,
                        this.rouletteBetsBuilder.getLineBet(bet.index).betAmount);
                } else if (bet instanceof ColumnBet) {
                    this.rouletteBetsBuilder.addColumnBet(bet.index, bet.betAmount);
                    this.chipsStacks.setColumnBetChipsStack(bet.index,
                        this.rouletteBetsBuilder.getColumnBet(bet.index).betAmount);
                } else if (bet instanceof DozenBet) {
                    this.rouletteBetsBuilder.addDozenBet(bet.index, bet.betAmount);
                    this.chipsStacks.setDozenBetChipsStack(bet.index,
                        this.rouletteBetsBuilder.getDozenBet(bet.index).betAmount);
                } else if (bet instanceof LowHighNumbersBet) {
                    this.rouletteBetsBuilder.addLowHighNumbersBet(bet.lowNumbers, bet.betAmount);
                    this.chipsStacks.setLowHighNumbersBetChipsStack(bet.lowNumbers,
                        this.rouletteBetsBuilder.getLowHighNumbersBet(bet.lowNumbers).betAmount);
                } else if (bet instanceof EvenOddBet) {
                    this.rouletteBetsBuilder.addEvenOddBet(bet.evenNumbers, bet.betAmount);
                    this.chipsStacks.setEvenOddBetChipsStack(bet.evenNumbers,
                        this.rouletteBetsBuilder.getEvenOddBet(bet.evenNumbers).betAmount);
                } else if (bet instanceof ColorBet) {
                    this.rouletteBetsBuilder.addColorBet(bet.redColor, bet.betAmount);
                    this.chipsStacks.setColorBetsChipsStacks(bet.redColor,
                        this.rouletteBetsBuilder.getColorBet(bet.redColor).betAmount);
                }
            }
        }
        this.infoSnackBar.show("All bets restored.", this.RESTORE_BETS_POPUP_POSITION);
    }

    public removeLastBet(): void {
        try {
            const lastBet = this.rouletteBetsHistory.pop();
            if (lastBet) {
                if (lastBet instanceof StraightBet) {
                    this.rouletteBetsBuilder.decreaseStraightBet(lastBet.chosenNumber, lastBet.betAmount);
                    this.chipsStacks.setStraightBetChipsStack(lastBet.chosenNumber,
                        this.rouletteBetsBuilder.getStraightBet(lastBet.chosenNumber).betAmount);
                } else if (lastBet instanceof SplitBet) {
                    this.rouletteBetsBuilder.decreaseSplitBet(lastBet.index, lastBet.betAmount);
                    this.chipsStacks.setSplitBetChipsStack(lastBet.index,
                        this.rouletteBetsBuilder.getSplitBet(lastBet.index).betAmount);
                } else if (lastBet instanceof CornerBet) {
                    this.rouletteBetsBuilder.decreaseCornerBet(lastBet.index, lastBet.betAmount);
                    this.chipsStacks.setCornerBetChipsStack(lastBet.index,
                        this.rouletteBetsBuilder.getCornerBet(lastBet.index).betAmount);
                } else if (lastBet instanceof StreetBet) {
                    this.rouletteBetsBuilder.decreaseStreetBet(lastBet.index, lastBet.betAmount);
                    this.chipsStacks.setStreetBetChipsStack(lastBet.index,
                        this.rouletteBetsBuilder.getStreetBet(lastBet.index).betAmount);
                } else if (lastBet instanceof LineBet) {
                    this.rouletteBetsBuilder.decreaseLineBet(lastBet.index, lastBet.betAmount);
                    this.chipsStacks.setLineBetChipsStack(lastBet.index,
                        this.rouletteBetsBuilder.getLineBet(lastBet.index).betAmount);
                } else if (lastBet instanceof ColumnBet) {
                    this.rouletteBetsBuilder.decreaseColumnBet(lastBet.index, lastBet.betAmount);
                    this.chipsStacks.setColumnBetChipsStack(lastBet.index,
                        this.rouletteBetsBuilder.getColumnBet(lastBet.index).betAmount);
                } else if (lastBet instanceof DozenBet) {
                    this.rouletteBetsBuilder.decreaseDozenBet(lastBet.index, lastBet.betAmount);
                    this.chipsStacks.setDozenBetChipsStack(lastBet.index,
                        this.rouletteBetsBuilder.getDozenBet(lastBet.index).betAmount);
                } else if (lastBet instanceof LowHighNumbersBet) {
                    this.rouletteBetsBuilder.decreaseLowHighNumbersBet(lastBet.lowNumbers, lastBet.betAmount);
                    this.chipsStacks.setLowHighNumbersBetChipsStack(lastBet.lowNumbers,
                        this.rouletteBetsBuilder.getLowHighNumbersBet(lastBet.lowNumbers).betAmount);
                } else if (lastBet instanceof EvenOddBet) {
                    this.rouletteBetsBuilder.decreaseEvenOddBet(lastBet.evenNumbers, lastBet.betAmount);
                    this.chipsStacks.setEvenOddBetChipsStack(lastBet.evenNumbers,
                        this.rouletteBetsBuilder.getEvenOddBet(lastBet.evenNumbers).betAmount);
                } else if (lastBet instanceof ColorBet) {
                    this.rouletteBetsBuilder.decreaseColorBet(lastBet.redColor, lastBet.betAmount);
                    this.chipsStacks.setColorBetsChipsStacks(lastBet.redColor,
                        this.rouletteBetsBuilder.getColorBet(lastBet.redColor).betAmount);
                }

                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                this.infoSnackBar.show("Last bet removed.", this.NO_LAST_BET_POPUP_POSITION);
            } else {
                this.infoSnackBar.show("No bets to remove.", this.NO_LAST_BET_POPUP_POSITION);
            }
        } catch (err) {
            this.removeLastBet();
        }
    }

    private createAndAddStraightBetsPanel(): void {
        this.straightBetsPanel = new StraightBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_BUTTONS_OFFSET_Y, this.STRAIGHT_BETS_OFFSET_FROM_ZERO);
        this.straightBetsPanel.subscribeOnClicked((selectedNumber) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                const betAmountBeforeBet = this.rouletteBetsBuilder.getStraightBet(selectedNumber).betAmount;
                this.rouletteBetsBuilder.addStraightBet(selectedNumber, this.betValue);
                const betAmountAfterBet = this.rouletteBetsBuilder.getStraightBet(selectedNumber).betAmount;
                this.rouletteBetsHistory.push(new StraightBet(selectedNumber, betAmountAfterBet -  betAmountBeforeBet));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                this.chipsStacks.setStraightBetChipsStack(selectedNumber, betAmountAfterBet);
                this.infoSnackBar.show(betAmountAfterBet + " " + config.displayCurrency,
                    this.straightBetsPanel.getButtonPosition(selectedNumber));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.straightBetsPanel.getButtonPosition(selectedNumber));
            }
        });
        this.addChild(this.straightBetsPanel);
    }

    private createAndAddSplitBetsPanel(): void {
        let selectedNumbersCopy;
        this.splitBetsPanel = new SplitBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_BUTTONS_OFFSET_Y, this.STRAIGHT_BETS_OFFSET_FROM_ZERO);
        this.splitBetsPanel.subscribeOnClicked((selectedIndex: number) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addSplitBet(selectedIndex, this.betValue);
                this.rouletteBetsHistory.push(new SplitBet(selectedIndex, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getSplitBet(selectedIndex).betAmount;
                this.chipsStacks.setSplitBetChipsStack(selectedIndex, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.splitBetsPanel.getButtonPosition(selectedIndex));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.splitBetsPanel.getButtonPosition(selectedIndex));
            }
        });
        this.splitBetsPanel.subscribeOnPointerDown((selectedIndex: number, selectedNumbers: Array<number>) => {
            this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
            selectedNumbersCopy = selectedNumbers;
        });
        this.splitBetsPanel.subscribeOnPointerUp(() => {
            this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
        });
        this.addChild(this.splitBetsPanel);
    }

    private createAndAddCornerBetsPanel(): void {
        let selectedNumbersCopy;
        this.cornerBetsPanel = new CornerBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_BUTTONS_OFFSET_Y, this.STRAIGHT_BETS_OFFSET_FROM_ZERO);
        this.cornerBetsPanel.subscribeOnClicked((selectedIndex: number) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addCornerBet(selectedIndex, this.betValue);
                this.rouletteBetsHistory.push(new CornerBet(selectedIndex, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getCornerBet(selectedIndex).betAmount;
                this.chipsStacks.setCornerBetChipsStack(selectedIndex, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.cornerBetsPanel.getButtonPosition(selectedIndex));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.cornerBetsPanel.getButtonPosition(selectedIndex));
            }
        });
        this.cornerBetsPanel.subscribeOnPointerDown((selectedIndex: number, selectedNumbers: Array<number>) => {
            this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
            selectedNumbersCopy = selectedNumbers;
        });
        this.cornerBetsPanel.subscribeOnPointerUp(() => {
            this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
        });
        this.addChild(this.cornerBetsPanel);
    }

    private createAndAddLineBetsPanel(): void {
        let selectedNumbersCopy;
        this.lineBetsPanel = new LineBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_BUTTONS_OFFSET_Y, this.STRAIGHT_BETS_OFFSET_FROM_ZERO);
        this.lineBetsPanel.subscribeOnClicked((selectedIndex: number) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addLineBet(selectedIndex, this.betValue);
                this.rouletteBetsHistory.push(new LineBet(selectedIndex, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getLineBet(selectedIndex).betAmount;
                this.chipsStacks.setLineBetChipsStack(selectedIndex, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.lineBetsPanel.getButtonPosition(selectedIndex));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.lineBetsPanel.getButtonPosition(selectedIndex));
            }
        });
        this.lineBetsPanel.subscribeOnPointerDown((selectedIndex: number, selectedNumbers: Array<number>) => {
            this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
            selectedNumbersCopy = selectedNumbers;
        });
        this.lineBetsPanel.subscribeOnPointerUp(() => {
            this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
        });
        this.addChild(this.lineBetsPanel);
    }

    private createAndAddStreetBetsPanel(): void {
        let selectedNumbersCopy;
        this.streetBetsPanel = new StreetBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_BUTTONS_OFFSET_Y, this.STRAIGHT_BETS_OFFSET_FROM_ZERO);
        this.streetBetsPanel.subscribeOnClicked((selectedIndex: number) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addStreetBet(selectedIndex, this.betValue);
                this.rouletteBetsHistory.push(new StreetBet(selectedIndex, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getStreetBet(selectedIndex).betAmount;
                this.chipsStacks.setStreetBetChipsStack(selectedIndex, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.streetBetsPanel.getButtonPosition(selectedIndex));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.streetBetsPanel.getButtonPosition(selectedIndex));
            }
        });
        this.streetBetsPanel.subscribeOnPointerDown((selectedIndex: number, selectedNumbers: Array<number>) => {
            this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
            selectedNumbersCopy = selectedNumbers;
        });
        this.streetBetsPanel.subscribeOnPointerUp(() => {
            this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
        });
        this.addChild(this.streetBetsPanel);
    }

    private createAndAddColumnBetsPanel(): void {
        let selectedNumbersCopy;
        this.columnBetsPanel = new ColumnBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_BUTTONS_OFFSET_Y, this.STRAIGHT_BETS_OFFSET_FROM_ZERO);
        this.columnBetsPanel.subscribeOnClicked((selectedIndex: number) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addColumnBet(selectedIndex, this.betValue);
                this.rouletteBetsHistory.push(new ColumnBet(selectedIndex, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getColumnBet(selectedIndex).betAmount;
                this.chipsStacks.setColumnBetChipsStack(selectedIndex, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.columnBetsPanel.getButtonPosition(selectedIndex));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.columnBetsPanel.getButtonPosition(selectedIndex));
            }
        });
        this.columnBetsPanel.subscribeOnPointerDown((selectedIndex: number, selectedNumbers: Array<number>) => {
            this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
            selectedNumbersCopy = selectedNumbers;
        });
        this.columnBetsPanel.subscribeOnPointerUp(() => {
            this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
        });
        this.addChild(this.columnBetsPanel);
    }

    private createAndAddDozenBetsPanel(): void {
        let selectedNumbersCopy;
        this.dozenBetsPanel = new DozenBetsPanel(this.STRAIGHT_BETS_BUTTONS_OFFSET_X,
            this.STRAIGHT_BETS_OFFSET_FROM_ZERO, this.DOZEN_BETS_POSITION_Y);
        this.dozenBetsPanel.subscribeOnClicked((selectedIndex) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addDozenBet(selectedIndex, this.betValue);
                this.rouletteBetsHistory.push(new DozenBet(selectedIndex, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getDozenBet(selectedIndex).betAmount;
                this.chipsStacks.setDozenBetChipsStack(selectedIndex, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.dozenBetsPanel.getButtonPosition(selectedIndex));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.dozenBetsPanel.getButtonPosition(selectedIndex));
            }
        });
        this.dozenBetsPanel.subscribeOnPointerDown((selectedIndex: number, selectedNumbers: Array<number>) => {
            this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
            selectedNumbersCopy = selectedNumbers;
        });
        this.dozenBetsPanel.subscribeOnPointerUp(() => {
            this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
        });
        this.addChild(this.dozenBetsPanel);
    }

    private createAndAddLowHighNumbersBetsPanel(): void {
        let selectedNumbersCopy;
        this.lowHighNumbersBetsPanel = new LowHighNumbersBetsPanel(this.LOW_NUMBERS_BET_POSITION_X,
            this.HIGH_NUMBERS_BET_POSITION_X, this.EVEN_ODD_COLOR_LOW_HIGH_BETS_POSITION_Y);
        this.lowHighNumbersBetsPanel.subscribeOnClicked((lowNumbersSelected: boolean) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addLowHighNumbersBet(lowNumbersSelected, this.betValue);
                this.rouletteBetsHistory.push(new LowHighNumbersBet(lowNumbersSelected, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getLowHighNumbersBet(lowNumbersSelected).betAmount;
                this.chipsStacks.setLowHighNumbersBetChipsStack(lowNumbersSelected, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.lowHighNumbersBetsPanel.getButtonPosition(lowNumbersSelected));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.lowHighNumbersBetsPanel.getButtonPosition(lowNumbersSelected));
            }
        });
        this.lowHighNumbersBetsPanel.subscribeOnPointerDown(
            (evenNumbersSelected: boolean, selectedNumbers: Array<number>) => {
                this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
                selectedNumbersCopy = selectedNumbers;
            });
        this.lowHighNumbersBetsPanel.subscribeOnPointerUp(
            () => {
                this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
            });
        this.addChild(this.lowHighNumbersBetsPanel);
    }

    private createAndAddEvenOddBetsPanel(): void {
        let selectedNumbersCopy;
        this.evenOddBetsPanel = new EvenOddBetsPanel(this.EVEN_BET_POSITION_X,
            this.ODD_BET_POSITION_X, this.EVEN_ODD_COLOR_LOW_HIGH_BETS_POSITION_Y);
        this.evenOddBetsPanel.subscribeOnClicked((evenNumbersSelected: boolean) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addEvenOddBet(evenNumbersSelected, this.betValue);
                this.rouletteBetsHistory.push(new EvenOddBet(evenNumbersSelected, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getEvenOddBet(evenNumbersSelected).betAmount;
                this.chipsStacks.setEvenOddBetChipsStack(evenNumbersSelected, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.evenOddBetsPanel.getButtonPosition(evenNumbersSelected));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.evenOddBetsPanel.getButtonPosition(evenNumbersSelected));
            }
        });
        this.evenOddBetsPanel.subscribeOnPointerDown(
            (evenNumbersSelected: boolean, selectedNumbers: Array<number>) => {
                this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
                selectedNumbersCopy = selectedNumbers;
            });
        this.evenOddBetsPanel.subscribeOnPointerUp(
            () => {
                this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
            });
        this.addChild(this.evenOddBetsPanel);
    }

    private createAndAddColorBetsPanel(): void {
        let selectedNumbersCopy;
        this.colorBetsPanel = new ColorBetsPanel(this.RED_COLOR_BET_POSITION_X,
            this.BLACK_COLOR_BET_POSITION_X, this.EVEN_ODD_COLOR_LOW_HIGH_BETS_POSITION_Y);
        this.colorBetsPanel.subscribeOnClicked((redColorSelected: boolean) => {
            try {
                if ((this.betValue + this.rouletteBetsBuilder.getTotalBetAmount()) > this.balanceValue) {
                    throw new Error("Not enough " + config.displayCurrency + " on your balance.");
                }
                this.previousRouletteBetsHistory = [];
                this.rouletteBetsBuilder.addColorBet(redColorSelected, this.betValue);
                this.rouletteBetsHistory.push(new ColorBet(redColorSelected, this.betValue));
                Footer.getInstance().setTotalBetAmount(this.rouletteBetsBuilder.getTotalBetAmount());
                const betAmount = this.rouletteBetsBuilder.getColorBet(redColorSelected).betAmount;
                this.chipsStacks.setColorBetsChipsStacks(redColorSelected, betAmount);
                this.infoSnackBar.show(betAmount + " " + config.displayCurrency,
                    this.colorBetsPanel.getButtonPosition(redColorSelected));
            } catch (err) {
                this.infoSnackBar.show(err.message, this.colorBetsPanel.getButtonPosition(redColorSelected));
            }
        });
        this.colorBetsPanel.subscribeOnPointerDown(
            (evenNumbersSelected: boolean, selectedNumbers: Array<number>) => {
                this.straightBetsPanel.enableHighlightAndDisableButtons(selectedNumbers);
                selectedNumbersCopy = selectedNumbers;
            });
        this.colorBetsPanel.subscribeOnPointerUp(
            () => {
                this.straightBetsPanel.disableHighlightAndEnableButtons(selectedNumbersCopy);
            });
        this.addChild(this.colorBetsPanel);
    }

    private createAndAddChipsStacks(): void {
        // ChipsStacks picks the extremely small (or regular) token set itself based on config.
        if (config.smallBets && !config.extremelySmallBets) {
            this.chipsStacks = new SmallBetsChipsStacks(
                this.STRAIGHT_BETS_BUTTONS_OFFSET_X, this.STRAIGHT_BETS_BUTTONS_OFFSET_Y,
                this.STRAIGHT_BETS_OFFSET_FROM_ZERO, this.DOZEN_BETS_POSITION_Y,
                this.EVEN_ODD_COLOR_LOW_HIGH_BETS_POSITION_Y, this.LOW_NUMBERS_BET_POSITION_X,
                this.EVEN_BET_POSITION_X, this.RED_COLOR_BET_POSITION_X, this.BLACK_COLOR_BET_POSITION_X,
                this.ODD_BET_POSITION_X, this.HIGH_NUMBERS_BET_POSITION_X);
        } else {
            this.chipsStacks = new ChipsStacks(this.STRAIGHT_BETS_BUTTONS_OFFSET_X, this.STRAIGHT_BETS_BUTTONS_OFFSET_Y,
                this.STRAIGHT_BETS_OFFSET_FROM_ZERO, this.DOZEN_BETS_POSITION_Y,
                this.EVEN_ODD_COLOR_LOW_HIGH_BETS_POSITION_Y, this.LOW_NUMBERS_BET_POSITION_X,
                this.EVEN_BET_POSITION_X, this.RED_COLOR_BET_POSITION_X, this.BLACK_COLOR_BET_POSITION_X,
                this.ODD_BET_POSITION_X, this.HIGH_NUMBERS_BET_POSITION_X);
        }

        this.addChild(this.chipsStacks);
    }

    private createAndAddInfoSnackBar(): void {
        this.infoSnackBar = new InfoSnackBar();
        (window as any).infoSnackBar = this.infoSnackBar;
        this.addChild(this.infoSnackBar);
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/rouletteBetsPanel/implementations/RouletteBetsPanel.ts